import adobeLogo from '../assets/img/adobe.png';
import wirpoProjectImage from '../assets/img/bluedart.png';
import fidelityLogo from '../assets/img/fid.png';
import wiproLogo from '../assets/img/wipro.png';
import PDF from "../assets/doc/subhransu-fullstack-resume.pdf";

export const portfolioData = {
    about: {
        name: "Subhransu Maharana",
        role: "Senior Full Stack Engineer",
        experienceYear: "10+ years",
        currentRole: "Senior Application Engineer",
        currentCompany: "Adobe Systems",
        education: {
            degree: "B.Tech in Computer Science",
            university: "VSSUT, Burla"
        },
        bio: [
            "I am a Senior Application Engineer at Adobe Systems, specializing in architecting scalable cloud systems and enterprise data pipelines.",
            "With over a decade of experience, I bridge the gap between complex backend infrastructure and intuitive frontend user experiences. My expertise spans the modern JavaScript ecosystem (React, Node.js) and big data architecture (AWS Glue, Spark, Kafka).",
            "I focus on solving 'Cloud Physics' problems—optimizing costs, handling network constraints, and processing massive datasets with serverless architectures.",
            "Currently, I lead initiatives to automate high-volume data integration for Adobe's top-tier digital marketing clients, ensuring 99.9% reliability and SLA compliance.",
            "Beyond the code, I am a mentor, technical writer, and advocate for 'Infrastructure as Code' best practices."
        ]
    },
    home: {
        title: "Subhransu",
        subtitle: "Senior Full Stack Engineer",
        tagline: "Architecting Scalable Cloud Systems, Data Pipelines, and Enterprise UIs.",
        techStack: [
            {
                name: "React",
                className: "devicon-react-original",
            },
            {
                name: "Node.js",
                className: "devicon-nodejs-plain",
            },
            {
                name: "AWS",
                className: "devicon-amazonwebservices-plain-wordmark",
            },
            {
                name: "Python",
                className: "devicon-python-plain",
            }
        ],
        description: "A Senior Full Stack Engineer specializing in Enterprise System Design and Cloud Architecture."
    },
    social: [
        {
            name: 'Email',
            url: 'mailto:subhransumaharana@gmail.com',
            text: 'subhransumaharana@gmail.com',
            icon: 'email'
        },
        {
            name: 'LinkedIn',
            url: 'https://www.linkedin.com/in/smaharana/',
            text: 'Subhransu',
            icon: 'linkedin'
        },
        {
            name: 'GitHub',
            url: 'https://github.com/subhnet',
            text: 'subhnet',
            icon: 'github'
        },
        {
            name: 'Twitter',
            url: 'https://twitter.com/subhmaharana',
            text: '@subhmaharana',
            icon: 'twitter'
        },
        {
            name: 'Dev.to',
            url: 'https://dev.to/subhransu',
            text: 'subhransu',
            icon: 'devto'
        }
    ],
    resumeUrl: PDF,
    skills: {
        "Cloud & Architecture": [
            "AWS (Lambda, Glue, S3)", "Serverless Framework", "Microservices", "Docker", "CI/CD (GitHub Actions)"
        ],
        "Big Data": [
            "Apache Spark", "PySpark", "Hadoop", "Apache Kafka", "Apache Airflow"
        ],
        "Frontend Engineering": [
            "ReactJS (Hooks, Context)", "TypeScript", "Node.js", "Webpack/Parcel", "HTML5/CSS3"
        ],
        "Backend & API": [
            "Python (Flask/Boto3)", "Java SpringBoot", "GraphQL", "REST", "Adobe I/O Runtime"
        ],
        "Databases": [
            "PostgreSQL", "MySQL", "Redis", "DynamoDB"
        ]
    },
    blogs: [
        {
            date: "26 Apr 2020",
            title: "Realtime Chat app using Kafka, SpringBoot, ReactJS, and WebSockets",
            url: "https://dev.to/subhransu/realtime-chat-app-using-kafka-springboot-reactjs-and-websockets-lc",
            platform: "Dev.to"
        },
        {
            date: "01 Mar 2020",
            title: "Dockerizing Reactjs, nodejs and nginx server",
            url: "https://dev.to/subhransu/nevertheless-subhransu-maharana-coded-5eam",
            platform: "Dev.to"
        },
        {
            date: "09 Feb 2019",
            title: "Developing Chat App with Angular UI and Back-end in Golang",
            url: "https://medium.com/@subhransumaharana_40338/developing-chat-app-with-angular-ui-and-back-end-in-golang-be01de4cdef7",
            platform: "Medium"
        }
    ],
    experience: [
        {
            title: "Adobe Systems",
            timeline: "2019 - Present",
            logo: adobeLogo,
            designation: "Senior Application Engineer",
            techstack: [
                { URL: "https://nodejs.org/", name: "NodeJS" },
                { URL: "https://react.dev/", name: "React 18" },
                { URL: "https://aws.amazon.com/serverless/", name: "AWS Serverless" },
                { URL: "https://spark.apache.org/", name: "PySpark" },
                { URL: "https://airflow.apache.org/", name: "Apache Airflow" },
                { URL: "https://www.python.org/", name: "Python" },
                { URL: "https://developer.adobe.com/runtime/", name: "Adobe I/O" },
                { URL: "https://kafka.apache.org/", name: "Kafka" }
            ],
            projects: [
                {
                    title: "Hybrid Cloud Data Pipeline",
                    description: [
                        "Architected a hybrid ETL pipeline processing high-volume clickstream data (Abandoned Carts/Purchases), utilizing AWS Glue for serverless scale and ephemeral EC2 for legacy compatibility.",
                        "Designed an 'Infrastructure as Code' control plane using Python & Boto3 to dynamically provision resources based on CloudFormation exports, ensuring zero-configuration drift between environments.",
                        "Engineered stateful session replay logic to reconstruct user shopping journeys from raw logs, enabling a revenue-generating email retargeting stream with strict SLA compliance.",
                        "Reduced idle cloud compute costs by 99% via dynamic resource orchestration."
                    ]
                },
                {
                    title: "Enterprise Integration Platform (ACS ↔ D365)",
                    description: [
                        "Architected a self-service integration platform allowing non-technical users to synchronize millions of marketing records across regions (NA, EMEA, APAC).",
                        "Designed a dynamic orchestration engine using Apache Airflow, where DAGs are generated programmatically from DynamoDB configurations.",
                        "Engineered an ephemeral big data pipeline using AWS EMR (Spark) and Kafka (MSK) to handle backpressure and process high-volume transformations.",
                        "Built a secure CI/CD ecosystem integrating HashiCorp Vault for secret injection and CloudFormation for multi-region infrastructure replication."
                    ]
                },
                {
                    title: "Campaign Deployment Engine",
                    description: [
                        "Architected a serverless React/Node.js application on Adobe App Builder to automate the ingestion of marketing campaign rules.",
                        "Engineered a 'Cartesian Explosion' algorithm to transform condensed marketing logic into high-volume database records, reducing manual entry time by 90%.",
                        "Optimized frontend reliability using React Error Boundaries and Lazy Loading, achieving a 99.9% crash-free session rate for operations teams."
                    ]
                },
                {
                    title: "Serverless Asset Ingestion Pipeline",
                    description: [
                        "Architected a serverless media pipeline using Node.js Streams to synchronize 300MB+ media assets between AWS S3 and Adobe Experience Manager.",
                        "Engineered a memory-efficient 'Streaming Tunnel' that pipes data directly via byte-range requests, reducing application memory footprint by 90% and bypassing Gateway timeouts.",
                        "Implemented a Saga-based orchestration pattern to intelligently route ingestion jobs between synchronous and asynchronous processing."
                    ]
                },
                {
                    title: "Petabyte-Scale Data Reprocessing",
                    description: [
                        "Engineered a fault-tolerant MapReduce pipeline to correct and re-ingest millions of corrupted analytics hits daily, ensuring 100% data integrity.",
                        "Optimized distributed shuffling by implementing a custom 'Secondary Sort' strategy, enabling chronological session reconstruction without memory overhead.",
                        "Architected a modular plugin system using the Strategy Pattern to decouple business logic, reducing new feature lead time by 40%.",
                        "Utilized the Hadoop cluster as a distributed rate-limiter to safely re-ingest data into downstream APIs without triggering denial-of-service events."
                    ]
                },
                {
                    title: "Adobe Analytics Migration Platform",
                    description: [
                        "Architected a distributed system using Docker Swarm, Node.js, and Redis to migrate complex Adobe Analytics component trees across environments.",
                        "Engineered a recursive dependency resolution algorithm (DFS) to topologically sort and reconstruct nested assets (Segments/Metrics) at runtime.",
                        "Implemented a non-blocking Producer-Consumer architecture, separating high-concurrency API traffic from CPU-intensive migration tasks.",
                        "Designed centralized observability with distributed tracing (request-id) and resilient state management for resumable job execution."
                    ]
                }
            ]
        },
        {
            title: "Fidelity Investments",
            timeline: "March 2017 - May 2019",
            logo: fidelityLogo,
            designation: "Software Engineer",
            techstack: [
                { URL: "https://angular.io/", name: "Angular" },
                { URL: "https://www.typescriptlang.org/", name: "TypeScript" },
                { URL: "https://spring.io/", name: "Spring Boot" },
                { URL: "https://www.oracle.com/java/", name: "Java" }
            ],
            description: [
                "Architected a reusable 'Table Component Framework' for the eTax Application, adopted by multiple business units to standardize dynamic content rendering.",
                "Engineered lazy-loading modules and optimized frontend bundles, resulting in an 85% reduction in initial web app loading time.",
                "Developed and secured robust RESTful APIs using Spring Boot, integrating with Oracle databases for high-throughput financial data processing.",
                "Led the migration of legacy JSP interfaces to modern Angular architecture, significantly improving maintainability and user experience."
            ]
        },
        {
            title: "Wipro",
            timeline: "April 2016 - Feb 2017",
            logo: wiproLogo,
            designation: "Project Engineer",
            projectImage: wirpoProjectImage,
            techstack: [
                { URL: "https://www.java.com/", name: "Java" },
                { URL: "https://spring.io/", name: "Spring MVC" },
                { URL: "https://developers.google.com/maps", name: "Google Maps API" }
            ],
            projects: [
                {
                    title: "Bluedart Logistics Revamp",
                    description: [
                        "Key contributor to the full-stack revamp of Bluedart's shipment tracking and booking application.",
                        "Integrated Google Maps API to calculate shortest delivery paths and locate service stations, directly improving logistics efficiency.",
                        "Developed SOAP-based web services to orchestrate data flow between third-party vendors and internal Oracle legacy systems."
                    ]
                }
            ]
        }
    ]
};